import { Component, Injector, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CognitoService } from 'app/core/http/services/cognito.service';
import { OrganizationSubUsersService } from 'app/core/http/services/organizationSubUsers.service';
import { OrganizationUser, Permissions } from 'app/core/interfaces/organizationUser';
import { User } from 'app/core/interfaces/user';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import * as moment from 'moment';

@Component({
  selector: 'page-user-registration',
  templateUrl: './users-registration.component.html',
  styleUrls: ['./users-registration.component.scss'],
})
export class OrganizationUserRegistrationComponent extends BaseComponent implements OnInit {
  public submitted = false;
  public successLogin = false;
  public form!: FormGroup;
  public hide: boolean = true;
  private organization: any;
  public title = 'Cadastro de usuário';

  public permissions: Permissions[] = [
    'DASHBOARD:manage',
    'SCHEDULE:manage',
    'PRACTITIONERS:manage',
    'SUBUSERS:manage',
    'ENCOUNTER:manage',
    'HEALTHCARES:manage',
    'PAYMENT_CONFIG:manage',
    'AGREEMENTS:manage',
    'MY_DATA:manage',
  ];

  constructor(
    private injector: Injector,
    private fb: FormBuilder,
    private cognitoService: CognitoService,
    private organizationSubUsersService: OrganizationSubUsersService
  ) {
    super(injector);
  }

  validatePassword(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    const hasNumber = /[0-9]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasSpecial = /[^\w\s]/.test(password);

    if (!hasNumber || !hasUpper || !hasLower || !hasSpecial) {
      return { invalidPassword: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        email: new FormControl(null, [Validators.required, Validators.email]),
        name: new FormControl(null, [Validators.required, Validators.pattern(/^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ ]*$/)]),
        occupation: new FormControl(null, [Validators.required]),
        password: new FormControl(null, [Validators.required, this.validatePassword]),
        password_confirmation: new FormControl(null, [Validators.required]),
        type: new FormControl('subOrganization', [Validators.required]),
      },
      { validators: this.matchPassword }
    );

    this.organization = this.currentUser.user;
  }

  getOccupationMessage(): string {
    const occupation = this.form.get('occupation')?.value;
    if (occupation === 'Administrador') {
      return 'Este cargo terá acesso a todas as funcionalidades.';
    } else if (occupation === 'Coordenador') {
      return 'Este cargo terá acesso a todas as funcionalidades, exceto a aba "Meus Dados".';
    } else if (occupation === 'Colaborador') {
      return 'Este cargo terá acesso apenas à aba "Agenda" e "Agendados".';
    }
    return '';
  }

  register() {
    if (this.form.invalid) {
      this.submitted = true;
      this.successLogin = false;
      return;
    }

    let selectedPermissions: Permissions[] = [];
    const occupation = this.form.value.occupation;

    if (occupation === 'Administrador') {
      selectedPermissions = [...this.permissions];
    } else if (occupation === 'Coordenador') {
      selectedPermissions = this.permissions.filter((perm) => !perm.startsWith('MY_DATA'));
    } else if (occupation === 'Colaborador') {
      selectedPermissions = this.permissions.filter((perm) => perm.startsWith('SCHEDULE') || perm.startsWith('ENCOUNTER'));
    }

    const cognitoData: User = {
      email: this.form.value.email,
      password: this.form.value.password,
      type: this.form.value.type,
    };

    this.cognitoService
      .signUpSubUser(cognitoData)
      .then((response) => {
        const userData: OrganizationUser = {
          email: this.form.value.email,
          type: this.form.value.type,
          organizationReferenceSub: this.organization.token_id,
          organizationReferenceId: this.organization.id,
          avatar: 'https://img001.prntscr.com/file/img001/du4dU63JSgiXTB3a8bhK7Q.png',
          userSub: response.userId,
          permissions: selectedPermissions,
          active: true,
          name: this.form.value.name,
          createdDate: moment().format(),
        };

        this.organizationSubUsersService.createSubUser(userData).subscribe({
          next: (organizationSubUser: OrganizationUser) => {
            this.submitted = true;
            this.successLogin = true;

            this.showSuccessMessage = true;
            this.showErrorMessage = false;
          },
          error: (err) => {
            this.messageError = err.message;
            this.showSuccessMessage = false;
            this.showErrorMessage = true;
          },
        });
      })
      .catch((error) => {
        console.error('Erro no cadastro:', error);
        this.successLogin = false;

        this.messageError = error.message;
        this.showSuccessMessage = false;
        this.showErrorMessage = true;
      });
  }

  private matchPassword(AC: AbstractControl) {
    const password = AC.get('password')?.value;
    const confirmPassword = AC.get('password_confirmation')?.value;

    if (password !== confirmPassword) {
      AC.get('password_confirmation')?.setErrors({ mismatch: true });
      return { mismatch: true };
    }
    return null;
  }
}
